import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import type { ClavePermiso, ClaveRol } from '@copathy/shared';
import { useMe, useLogout, usePermisos } from '../lib/auth';
import { MigasContext, type Miga } from '../lib/migas';
import { Marca } from './Marca';
import { LimiteDeError } from './LimiteDeError';
import {
  Ajustes,
  Brujula,
  Chevrones,
  Edificio,
  Enviar,
  Escudo,
  Grafico,
  Lista,
  Menu,
  Panel,
  Personas,
  Salir,
  Usuario,
} from './iconos';

type Icono = ComponentType<{ size?: number; className?: string }>;

/**
 * Ámbito del rol: es el eje que reparte el menú.
 *
 * Polaris reparte por «superficie» —la plataforma interna y la app del negocio
 * no comparten menú—. Acá el eje equivalente es a quién pertenece el rol:
 * Copathy opera la plataforma, la empresa cliente solo mira sus resultados, y
 * el trabajador responde. Son tres aplicaciones distintas con la misma puerta.
 */
type Ambito = 'copathy' | 'empresa' | 'trabajador';

const AMBITO_DE_ROL: Record<ClaveRol, Ambito> = {
  admin_copathy: 'copathy',
  consultor_copathy: 'copathy',
  admin_empresa: 'empresa',
  trabajador: 'trabajador',
};

type Seccion = {
  texto: string;
  /** Rótulo para la barra del móvil, donde no cabe el largo. */
  corto: string;
  a: string;
  icono: Icono;
  permiso?: ClavePermiso;
  /** Basta con UNO de la lista. Configuración agrupa cosas con permisos distintos. */
  permisoAlguno?: ClavePermiso[];
  ambitos: Ambito[];
};

/**
 * El ORDEN es el del día de trabajo, no el alfabético ni el del modelo de datos.
 *
 * Para Copathy: primero lo que se abre al llegar —la cola de solicitudes, que si
 * se atrasa deja a alguien sin poder responder—, después lo que se arma
 * —cuestionarios y aplicaciones—, después con qué se trabaja, y al final lo que
 * se mira de vez en cuando.
 */
const SECCIONES: Seccion[] = [
  { texto: 'Inicio', corto: 'Inicio', a: '/', icono: Panel, ambitos: ['copathy', 'empresa', 'trabajador'] },

  // ── Copathy ──
  { texto: 'Cuestionarios', corto: 'Cuest.', a: '/cuestionarios', icono: Lista, permiso: 'cuestionarios.ver', ambitos: ['copathy'] },
  { texto: 'Aplicaciones', corto: 'Aplic.', a: '/aplicaciones', icono: Enviar, permiso: 'aplicaciones.ver', ambitos: ['copathy'] },
  { texto: 'Clientes', corto: 'Clientes', a: '/clientes', icono: Edificio, permiso: 'empresas.ver', ambitos: ['copathy'] },
  { texto: 'Programas', corto: 'Program.', a: '/programas', icono: Brujula, permiso: 'empresas.ver', ambitos: ['copathy'] },
  { texto: 'Resultados', corto: 'Datos', a: '/resultados', icono: Grafico, permiso: 'resultados.ver_todas_empresas', ambitos: ['copathy'] },
  // Usuarios incluye la cola de solicitudes como pestaña: una solicitud es una
  // cuenta que todavía no existe, y tenerlas en dos entradas del menú obligaba a
  // recordar en cuál mirar.
  { texto: 'Usuarios', corto: 'Usuarios', a: '/usuarios', icono: Usuario, permiso: 'usuarios.administrar', ambitos: ['copathy'] },
  { texto: 'Auditoría', corto: 'Auditoría', a: '/auditoria', icono: Escudo, permiso: 'auditoria.ver', ambitos: ['copathy'] },

  // ── Empresa cliente ──
  { texto: 'Resultados', corto: 'Datos', a: '/mis-resultados', icono: Grafico, permiso: 'resultados.ver_propia_empresa', ambitos: ['empresa'] },
  { texto: 'Participación', corto: 'Particip.', a: '/participacion', icono: Personas, permiso: 'participacion.ver', ambitos: ['empresa'] },

  // ── Trabajador ──
  { texto: 'Mis actividades', corto: 'Activid.', a: '/actividades', icono: Lista, permiso: 'actividades.responder', ambitos: ['trabajador'] },
  { texto: 'Mi historial', corto: 'Historial', a: '/historial', icono: Grafico, permiso: 'historial.ver_propio', ambitos: ['trabajador'] },

  {
    texto: 'Configuración',
    corto: 'Config.',
    a: '/configuracion',
    icono: Ajustes,
    permisoAlguno: ['usuarios.administrar', 'catalogos.administrar', 'codigos.administrar'],
    ambitos: ['copathy', 'empresa'],
  },
];

const NOMBRE_ROL: Record<string, string> = {
  admin_copathy: 'Administrador Copathy',
  consultor_copathy: 'Consultor Copathy',
  admin_empresa: 'Administrador de empresa',
  trabajador: 'Trabajador',
};

/**
 * Shell de la aplicación: **paneles flotando sobre un lienzo**.
 *
 * El logotipo de Copathy no tiene una sola esquina — es un círculo y dos
 * cápsulas—, así que la interfaz sigue esa forma en vez de la retícula de cajas
 * pegadas con filetes que trae por defecto un panel de administración:
 *
 * · No hay franja de encabezado de borde a borde. El fondo es el lienzo marino
 *   claro, y el menú es un panel **marino** redondeado con margen, separado.
 * · El logotipo va sobre una **placa blanca** dentro del panel marino: el
 *   isotipo es un círculo marino, así que sobre marino desaparecería. La placa
 *   no es decoración — es lo que lo hace legible.
 * · Los controles de la barra son **círculos** sobre el lienzo, no botones
 *   dentro de una barra oscura.
 * · El ítem activo del menú es una **cápsula completa**, que es literalmente la
 *   forma de los brazos del logo.
 * · Un resplandor naranja muy tenue le da profundidad al lienzo sin ruido.
 */
export function Shell() {
  const { usuario } = useMe();
  const permisos = usePermisos();
  const salir = useLogout();
  const location = useLocation();

  const [migas, setMigas] = useState<Miga[]>([]);
  const [menuMovil, setMenuMovil] = useState(false);
  const [colapsado, setColapsado] = useState(() => {
    try {
      return localStorage.getItem('nav-colapsado') === '1';
    } catch {
      return false; // modo privado
    }
  });

  // El scroll vive dentro de <main>, no en el body: si no, la barra del móvil
  // aparece y desaparece y el alto de la pantalla salta mientras se navega.
  useEffect(() => {
    document.body.classList.add('app-bloqueada');
    return () => document.body.classList.remove('app-bloqueada');
  }, []);

  // Cambiar de ruta cierra el menú del móvil: si no, tapa lo que se acaba de abrir.
  useEffect(() => setMenuMovil(false), [location.pathname]);

  if (!usuario) return null;

  const ambito = AMBITO_DE_ROL[usuario.rol];
  const visibles = SECCIONES.filter(
    (s) =>
      s.ambitos.includes(ambito) &&
      (!s.permiso || permisos.includes(s.permiso)) &&
      (!s.permisoAlguno || s.permisoAlguno.some((p) => permisos.includes(p))),
  );

  // El filtro va por ruta y no por posición: mover un ítem en la lista de arriba
  // no puede cambiar dónde termina en pantalla.
  const configuracion = visibles.find((s) => s.a === '/configuracion');
  const delTrabajo = visibles.filter((s) => s.a !== '/configuracion');
  const tabsMovil = delTrabajo.slice(0, 4);

  function alternarColapso() {
    setColapsado((c) => {
      const v = !c;
      try {
        localStorage.setItem('nav-colapsado', v ? '1' : '0');
      } catch {
        /* modo privado */
      }
      return v;
    });
  }

  /*
    Cápsula completa para el ítem activo: la forma de los brazos del logo.

    Texto MARINO sobre el naranja, no blanco. Blanco sobre #FF6C0D da 2,84:1 de
    contraste — por debajo del mínimo accesible de 4,5:1—; marino oscuro da
    5,50:1. Y de paso es más de marca: el logo nunca pone blanco sobre naranja,
    pone naranja sobre marino.
    Sobre el panel marino, el inactivo va en blanco al 70 % — con `text-white/50`
    el menú se leía apagado, y con blanco puro los nueve ítems competían entre sí
    y con el activo.
  */
  const claseNav = ({ isActive }: { isActive: boolean }) =>
    `group relative flex items-center gap-3 rounded-full px-3.5 py-2.5 text-sm font-semibold transition ${
      colapsado ? 'justify-center' : ''
    } ${
      isActive
        ? 'bg-naranja-500 text-marino-900 shadow-[0_6px_18px_-6px_var(--color-naranja-500)]'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`;

  const claseControl =
    'grid h-10 w-10 place-items-center rounded-full border border-marino-200/70 bg-white text-marino-500 transition hover:border-marino-300 hover:text-marino-800';

  return (
    <MigasContext.Provider value={{ migas, setMigas }}>
      <div className="relative flex h-dvh overflow-hidden bg-marino-50">
        {/* Resplandor del lienzo. Le da profundidad sin agregar un solo borde. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -right-32 h-[34rem] w-[34rem] rounded-full opacity-25 blur-3xl"
          style={{
            background: 'radial-gradient(circle, var(--color-naranja-300) 0%, transparent 70%)',
          }}
        />

        {/* ── Menú lateral: panel flotante, no una columna pegada ── */}
        <aside
          className={`relative z-10 hidden shrink-0 flex-col transition-[width] duration-200 md:flex ${
            colapsado ? 'w-[5.5rem]' : 'w-64'
          }`}
        >
          <div className="m-3 mr-0 flex min-h-0 flex-1 flex-col rounded-[var(--radius-panel)] bg-marino-700 shadow-[0_14px_34px_-18px_var(--color-marino-900)]">
            <button
              onClick={alternarColapso}
              aria-label={colapsado ? 'Expandir menú' : 'Plegar menú'}
              title={colapsado ? 'Expandir menú' : 'Plegar menú'}
              className={`flex h-[5.25rem] w-full shrink-0 items-center transition ${
                colapsado ? 'justify-center px-3' : 'px-4'
              }`}
            >
              {/*
                Placa blanca detrás de la marca.

                No es adorno: el isotipo ES un círculo marino, así que sobre el
                panel marino se perdería. Sobre blanco recupera su contraste
                original y además queda como el punto más luminoso del menú, que
                es donde debe estar la marca.

                Plegado va el ISOTIPO y desplegado el LOGOTIPO: el logotipo
                completo achicado deja de leerse. Son dos piezas distintas.
              */}
              <span
                className={`flex items-center justify-center bg-white transition-all ${
                  colapsado
                    ? 'h-14 w-14 rounded-full'
                    : 'h-14 w-full rounded-[var(--radius-tarjeta)] px-4'
                }`}
              >
                <Marca tamano="sm" variante={colapsado ? 'isotipo' : 'logotipo'} />
              </span>
            </button>

            <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-2">
              {delTrabajo.map((s) => (
                <NavLink
                  key={s.a}
                  to={s.a}
                  end={s.a === '/'}
                  title={colapsado ? s.texto : undefined}
                  className={claseNav}
                >
                  <s.icono size={20} className="shrink-0" />
                  {!colapsado && <span className="truncate">{s.texto}</span>}
                </NavLink>
              ))}
            </nav>

            {/*
              Configuración al PIE y con otra forma: los demás ítems son el
              trabajo del día y este se toca al montar y casi nunca más. Fijado
              abajo, además no se va con el scroll cuando el menú crezca.
            */}
            {configuracion && (
              <div className="shrink-0 px-3 pb-2">
                <NavLink
                  to={configuracion.a}
                  title={colapsado ? configuracion.texto : undefined}
                  className={({ isActive }) =>
                    `flex items-center justify-center gap-2.5 rounded-full px-3 py-3 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-naranja-500 text-marino-900'
                        : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                    }`
                  }
                >
                  <configuracion.icono size={20} className="shrink-0" />
                  {!colapsado && <span>{configuracion.texto}</span>}
                </NavLink>
              </div>
            )}

            <button
              onClick={alternarColapso}
              aria-label={colapsado ? 'Expandir menú' : 'Plegar menú'}
              className="shrink-0 rounded-b-[var(--radius-panel)] border-t border-white/10 px-3 py-3 text-left transition hover:bg-white/5"
            >
              <span
                className={`flex items-center gap-3 px-1 text-sm font-medium text-white/50 ${
                  colapsado ? 'justify-center' : ''
                }`}
              >
                <Chevrones
                  size={20}
                  className={`shrink-0 transition-transform ${colapsado ? 'rotate-180' : ''}`}
                />
                {!colapsado && <span>Plegar menú</span>}
              </span>
            </button>
          </div>
        </aside>

        {/* ── Columna de contenido ── */}
        <div className="relative z-10 flex min-w-0 flex-1 flex-col">
          {/* Barra: transparente sobre el lienzo, sin franja de borde a borde. */}
          <header className="flex h-[4.5rem] shrink-0 items-center gap-3 px-4 md:px-6">
            <div className="min-w-0 md:hidden">
              <Marca tamano="sm" variante="isotipo" />
            </div>

            <div className="hidden min-w-0 flex-1 md:block">
              {migas.length > 0 ? (
                <nav aria-label="Ruta" className="flex items-center gap-2 text-sm">
                  {migas.map((m, i) => {
                    const ultimo = i === migas.length - 1;
                    return (
                      <span key={`${m.texto}-${i}`} className="flex items-center gap-2">
                        {i > 0 && <span className="text-marino-300">/</span>}
                        {m.a && !ultimo ? (
                          <Link to={m.a} className="text-marino-400 hover:text-marino-700">
                            {m.texto}
                          </Link>
                        ) : (
                          <span className="font-bold text-marino-800">{m.texto}</span>
                        )}
                      </span>
                    );
                  })}
                </nav>
              ) : (
                <span className="text-sm text-marino-400">
                  <span className="font-bold text-marino-800">{usuario.nombre}</span>
                  <span className="mx-2 text-marino-300">·</span>
                  {usuario.empresaNombre}
                </span>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {/* Pastilla de identidad: redonda, sobre el lienzo. */}
              <div className="hidden items-center gap-2.5 rounded-full border border-marino-200/70 bg-white py-1.5 pr-4 pl-1.5 lg:flex">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-marino-700 text-xs font-bold text-white">
                  {usuario.nombre
                    .split(' ')
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join('')
                    .toUpperCase()}
                </span>
                <span className="leading-tight">
                  <span className="block text-sm font-semibold text-marino-800">
                    {usuario.nombre}
                  </span>
                  <span className="block text-[11px] text-marino-400">
                    {NOMBRE_ROL[usuario.rol] ?? usuario.rol}
                  </span>
                </span>
              </div>

              <Link to="/perfil" title="Mi perfil" aria-label="Mi perfil" className={`${claseControl} hidden md:grid`}>
                <Usuario size={18} />
              </Link>
              <button
                onClick={() => void salir()}
                title="Salir"
                aria-label="Salir"
                className={`${claseControl} hidden md:grid`}
              >
                <Salir size={18} />
              </button>
            </div>
          </header>

          {/* Móvil: tira de migas. */}
          {migas.length > 0 && (
            <div className="flex shrink-0 items-center gap-1.5 overflow-x-auto px-4 pb-2 text-xs font-medium whitespace-nowrap text-marino-400 md:hidden">
              {migas.map((m, i) => (
                <span key={`${m.texto}-${i}`} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-marino-300">/</span>}
                  {m.a && i < migas.length - 1 ? (
                    <Link to={m.a}>{m.texto}</Link>
                  ) : (
                    <span className="font-bold text-marino-800">{m.texto}</span>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* pb-28 en movil: la barra de pestanas flota encima y taparia el final
                del contenido. */}
          <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-28 md:px-6 md:pb-6">
            {/*
              La frontera de error va acá adentro y no envolviendo al Shell: si
              una pantalla se cae, el menú tiene que seguir en pie para poder
              salir de ella. La `key` por ruta reinicia el error al navegar.
            */}
            <LimiteDeError key={location.pathname}>
              <Outlet />
            </LimiteDeError>
          </main>
        </div>

        {/* ── Barra de pestañas (móvil): flotante y en cápsula ── */}
        {/* Marino como el menú de escritorio: el cromo de la aplicación es
            marino en todas partes, y el contenido claro. */}
        <nav className="fixed inset-x-3 bottom-3 z-20 flex items-center justify-around rounded-full bg-marino-700/95 px-1.5 py-1.5 shadow-[0_14px_30px_-12px_var(--color-marino-900)] backdrop-blur md:hidden">
          {tabsMovil.map((s) => (
            <NavLink
              key={s.a}
              to={s.a}
              end={s.a === '/'}
              className={({ isActive }) =>
                `flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-full px-1 py-2 text-[10px] font-semibold transition ${
                  isActive ? 'bg-naranja-500 text-marino-900' : 'text-white/65'
                }`
              }
            >
              <s.icono size={20} />
              <span className="truncate">{s.corto}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setMenuMovil(true)}
            className="flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-full px-1 py-2 text-[10px] font-semibold text-white/65"
          >
            <Menu size={20} />
            <span>Menú</span>
          </button>
        </nav>

        {/* ── Menú completo (móvil) ── */}
        {menuMovil && (
          <div className="fixed inset-0 z-40 flex flex-col bg-marino-50 md:hidden">
            <header className="flex h-[4.5rem] shrink-0 items-center justify-between px-4">
              <Marca tamano="sm" />
              <button
                onClick={() => setMenuMovil(false)}
                aria-label="Cerrar menú"
                className={claseControl}
              >
                ✕
              </button>
            </header>

            <nav className="flex-1 space-y-1.5 overflow-y-auto px-4 pb-4">
              {visibles.map((s) => (
                <NavLink
                  key={s.a}
                  to={s.a}
                  end={s.a === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-full px-5 py-3.5 font-semibold transition ${
                      isActive ? 'bg-naranja-500 text-marino-900' : 'bg-white text-marino-600'
                    }`
                  }
                >
                  <s.icono size={22} className="shrink-0" />
                  {s.texto}
                </NavLink>
              ))}
            </nav>

            <div className="shrink-0 rounded-t-[var(--radius-panel)] bg-white p-4">
              <div className="mb-3 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-marino-700 text-sm font-bold text-white">
                  {usuario.nombre
                    .split(' ')
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join('')
                    .toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-marino-800">
                    {usuario.nombre}
                  </span>
                  <span className="block truncate text-sm text-marino-400">
                    {NOMBRE_ROL[usuario.rol] ?? usuario.rol} · {usuario.empresaNombre}
                  </span>
                </span>
              </div>
              <button
                onClick={() => void salir()}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-marino-200 py-3 font-semibold text-marino-700 transition hover:bg-marino-50"
              >
                <Salir size={18} /> Salir
              </button>
            </div>
          </div>
        )}
      </div>
    </MigasContext.Provider>
  );
}
